// InlineEditField — value renderer with three display modes.
//
// `displayMode` (added 2026-05-20 for the Section E full View/Edit refactor):
//   - 'inline' (default, backward-compatible):
//       click value → input → blur/Enter saves; debounced 400ms; Esc cancels.
//       Field-level state, field-level save. Used everywhere outside the
//       Account Management Info tab (e.g. AM Collect review surface).
//   - 'view':
//       read-only dt/dd. If value is empty and the field is NOT protected
//       (no `onProposeChange`), the entire row is hidden. Sensitive fields
//       render `••••` with a Reveal button; tapping Reveal shows plaintext
//       for 5 seconds and then auto-hides. Protected fields (readOnly +
//       onProposeChange) always render with the "Change the rate" button,
//       even when empty.
//   - 'edit':
//       input is always visible (including for empty values). On change,
//       the field calls `onChange` (NOT `onSave`); the parent card holds
//       the draft state and persists via its own card-level Save button.
//       Sensitive fields use <input type="password"> with a toggle-eye
//       affordance so the AM can confirm what they typed.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { Edit2, Eye, EyeOff } from 'lucide-react';
import { useHideBlanks } from './hide-blanks-context';
import { useFacilityNa } from './facility-na-context';

type FieldType = 'text' | 'number' | 'date' | 'multiline' | 'select';
export type DisplayMode = 'inline' | 'view' | 'edit';

const REVEAL_AUTO_HIDE_MS = 5000;

/**
 * Informational chip beside a field label — see `badge` on
 * InlineEditFieldProps. Neutral on purpose: `proposalPending` owns the warning
 * colours, and "Hourly" is not a warning.
 */
function FieldBadge({ text }: { text: string }) {
  return (
    <span className="ml-2 inline-flex items-center rounded border border-tcs-border bg-tcs-raised px-1.5 py-0.5 text-[10px] font-medium text-tcs-text-secondary">
      {text}
    </span>
  );
}

export interface InlineEditFieldProps {
  /** Display label rendered above the value. */
  label: string;
  /** Current value. May be null/undefined; we render `placeholder` in that case. */
  value: string | number | null | undefined;
  /** Save handler. Should return `{ok, error}`; this component rolls back if `ok=false`.
   *  Optional only when `readOnly=true` (in which case the field never saves)
   *  or when `displayMode='edit'` (in which case the parent card handles save). */
  onSave?: (next: string | number | null) => Promise<{ ok: boolean; error: string | null }>;
  /** Input type. */
  type?: FieldType;
  /** Options for `type='select'`. */
  options?: Array<{ value: string; label: string }>;
  /** Placeholder shown when empty (display mode). */
  placeholder?: string;
  /** Set to true for sensitive fields (e.g. alarm codes) — value is masked until revealed. */
  sensitive?: boolean;
  /** Force read-only mode. */
  readOnly?: boolean;
  /** When `readOnly` + this is set, render a "Change the rate" button.
   *  ⛔ It opens the one rate-change process; this field never writes a rate. */
  onProposeChange?: () => void;
  /** When set, a pending RCP badge is shown next to the field. */
  proposalPending?: boolean;
  /** Make the dt/dd span both columns of a grid. */
  full?: boolean;
  /** Used by the parent to disambiguate save targets (debug only). */
  fieldKey?: string;
  /** For multiline. Approx height in rows. */
  rows?: number;
  /** Small caption under the value — for facts about the value the label
   *  can't carry (CW-O: a count of 0 that means "not yet recorded"). */
  hint?: string;
  /** Neutral chip beside the label, for a fact about the FIELD rather than the
   *  value (CW-Q Phase 6: the rate basis, so "Billed on actual hours" reads as
   *  a deliberate arrangement instead of a missing number). Rendered in all
   *  three display modes. Distinct styling from the `proposalPending` badge,
   *  which is a warning; this one is informational. */
  badge?: string;
  /** Display mode — see file header for the three cases. Defaults to 'inline'. */
  displayMode?: DisplayMode;
  /** Card-level edit mode: when set (and `displayMode='edit'`), value changes are
   *  propagated via this callback and `onSave` is suppressed (card handles save). */
  onChange?: (next: string | number | null) => void;
}

export function InlineEditField(props: InlineEditFieldProps) {
  const { displayMode = 'inline' } = props;
  if (displayMode === 'view') return <ViewModeField {...props} />;
  if (displayMode === 'edit') return <EditModeField {...props} />;
  return <InlineModeField {...props} />;
}

// ============================================================================
// View mode — read-only dt/dd, hide-when-empty unless protected.
// ============================================================================

function ViewModeField({
  label,
  value,
  sensitive = false,
  readOnly = false,
  onProposeChange,
  proposalPending = false,
  full = false,
  fieldKey,
  placeholder = '(not set)',
  hint,
  badge,
}: InlineEditFieldProps) {
  const [reveal, setReveal] = useState(false);
  const revealTimer = useRef<number | null>(null);
  const isEmpty = value == null || value === '';
  const isProtected = readOnly && !!onProposeChange;

  // Auto-hide sensitive reveal after 5 seconds.
  useEffect(() => {
    if (!reveal) return;
    revealTimer.current = window.setTimeout(() => setReveal(false), REVEAL_AUTO_HIDE_MS) as unknown as number;
    return () => {
      if (revealTimer.current) window.clearTimeout(revealTimer.current);
    };
  }, [reveal]);

  // Spec: in view mode, render only fields with a value, UNLESS the field is
  // protected (rate fields with onProposeChange always render so AM can propose).
  if (isEmpty && !isProtected) return null;

  const displayValue =
    isEmpty
      ? null
      : sensitive && !reveal
        ? '•'.repeat(Math.min(String(value).length, 8))
        : String(value);

  return (
    <div className={full ? 'md:col-span-2' : undefined} data-field={fieldKey} data-display-mode="view">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
        {badge && <FieldBadge text={badge} />}
        {proposalPending && (
          <span className="ml-2 inline-flex items-center rounded bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
            Proposal pending
          </span>
        )}
      </dt>
      <dd className="mt-1 flex items-center gap-2 text-sm text-tcs-text">
        <span className={sensitive ? 'font-mono' : 'whitespace-pre-wrap break-words'}>
          {displayValue ?? <span className="text-tcs-text-muted italic">{placeholder}</span>}
        </span>
        {sensitive && displayValue != null && (
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            className="text-xs text-tcs-text-secondary hover:text-tcs-text"
            aria-label={reveal ? `Hide ${label}` : `Reveal ${label}`}
          >
            {reveal ? 'hide' : 'reveal'}
          </button>
        )}
        {isProtected && (
          <button
            type="button"
            onClick={onProposeChange}
            className="ml-auto rounded border border-tcs-border bg-tcs-raised px-2 py-0.5 text-xs text-tcs-text hover:bg-tcs-surface"
          >
            Change the rate
          </button>
        )}
      </dd>
      {hint && <p className="mt-0.5 text-[11px] text-tcs-text-muted">{hint}</p>}
    </div>
  );
}

// ============================================================================
// Edit mode — input always visible, onChange propagates draft to parent card.
// ============================================================================

function EditModeField({
  label,
  value,
  type = 'text',
  options,
  placeholder = '(not set)',
  sensitive = false,
  readOnly = false,
  onProposeChange,
  proposalPending = false,
  full = false,
  fieldKey,
  rows = 3,
  hint,
  badge,
  onChange,
}: InlineEditFieldProps) {
  // Protected fields stay read-only with their propose-change affordance even
  // in card-edit mode. The card-level Save doesn't touch them.
  if (readOnly && onProposeChange) {
    return (
      <ViewModeField
        label={label}
        value={value}
        sensitive={sensitive}
        readOnly={readOnly}
        onProposeChange={onProposeChange}
        proposalPending={proposalPending}
        full={full}
        fieldKey={fieldKey}
        placeholder={placeholder}
        hint={hint}
        badge={badge}
      />
    );
  }

  const [showSensitive, setShowSensitive] = useState(false);
  const draftString = value == null ? '' : String(value);

  function handleChange(next: string) {
    if (!onChange) return;
    const trimmed = next; // keep raw; trim at save time
    const parsed: string | number | null =
      trimmed === ''
        ? null
        : type === 'number'
          ? (Number.isNaN(Number(trimmed)) ? trimmed : Number(trimmed))
          : trimmed;
    onChange(parsed);
  }

  const isPasswordMasked = sensitive && !showSensitive && type !== 'multiline' && type !== 'select';
  const inputType =
    type === 'date'
      ? 'date'
      : type === 'number'
        ? 'number'
        : isPasswordMasked
          ? 'password'
          : 'text';

  return (
    <div className={full ? 'md:col-span-2' : undefined} data-field={fieldKey} data-display-mode="edit">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
        {badge && <FieldBadge text={badge} />}
        {proposalPending && (
          <span className="ml-2 inline-flex items-center rounded bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
            Proposal pending
          </span>
        )}
      </dt>
      <dd className="mt-1.5">
        <div className="flex items-stretch gap-2">
          {type === 'multiline' ? (
            <textarea
              value={draftString}
              rows={rows}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              placeholder={placeholder}
            />
          ) : type === 'select' && options ? (
            <select
              value={draftString}
              onChange={(e) => handleChange(e.target.value)}
              className="rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
            >
              <option value="">— select —</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={inputType}
              value={draftString}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              placeholder={placeholder}
            />
          )}
          {sensitive && type !== 'multiline' && type !== 'select' && (
            <button
              type="button"
              onClick={() => setShowSensitive((s) => !s)}
              className="rounded border border-tcs-border bg-tcs-raised px-2 text-tcs-text-secondary hover:text-tcs-text"
              aria-label={showSensitive ? `Hide ${label}` : `Show ${label}`}
            >
              {showSensitive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {hint && <p className="mt-1 text-[11px] text-tcs-text-muted">{hint}</p>}
      </dd>
    </div>
  );
}

// ============================================================================
// Inline mode — the original click-to-edit behaviour (kept for backward compat).
// ============================================================================

function InlineModeField({
  label,
  value,
  onSave,
  type = 'text',
  options,
  placeholder = '(not set)',
  sensitive = false,
  readOnly = false,
  onProposeChange,
  proposalPending = false,
  full = false,
  fieldKey,
  rows = 3,
  // CW-Q Phase 6: `hint` and `badge` were declared on InlineEditFieldProps but
  // never destructured here, so anything passed to a DEFAULT-mode field was
  // silently dropped — view and edit mode rendered it, inline mode threw it
  // away. Nothing was passing hint in inline mode yet, so it was latent rather
  // than broken; the Financial summary card is the first caller, and it needs
  // both. Rendered below, matching ViewModeField's markup.
  hint,
  badge,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value == null ? '' : String(value));
  const [optimistic, setOptimistic] = useState<string | number | null | undefined>(value);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(false);
  const blurTimer = useRef<number | null>(null);
  const hideBlanks = useHideBlanks();
  const isNa = useFacilityNa(fieldKey);

  // Section E drop-in (2026-05-19): when the card's HideBlanksProvider has
  // hideBlanks=true, suppress this row entirely if the value is empty and
  // the row isn't being edited / isn't a protected field with a pending
  // proposal. Protected fields with onProposeChange stay visible so the
  // user can always propose a value even when the field is empty.
  //
  // 2026-05-22 (auto-promotion rewrite): NA-marked fields are also hidden
  // in read mode (they're effectively "answered as not-applicable", which
  // makes the read-mode rule symmetric with NULL). Edit mode still surfaces
  // them with an "N/A" pill so AMs can override.
  const isEmpty = value == null || value === '';
  if (hideBlanks && (isEmpty || isNa) && !editing && !proposalPending && !onProposeChange) {
    return null;
  }

  // Sync optimistic state when the parent's value changes (e.g. after refresh)
  useEffect(() => {
    setOptimistic(value);
    if (!editing) {
      setDraft(value == null ? '' : String(value));
    }
  }, [value, editing]);

  function startEdit() {
    if (readOnly) return;
    setDraft(value == null ? '' : String(value));
    setEditing(true);
    setError(null);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
    setDraft(value == null ? '' : String(value));
  }

  async function commit() {
    if (blurTimer.current) {
      window.clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
    const trimmed = draft.trim();
    const next: string | number | null =
      trimmed === ''
        ? null
        : type === 'number'
          ? Number(trimmed)
          : trimmed;

    // No-op? bail.
    const prevString = optimistic == null ? '' : String(optimistic);
    if (prevString === (next == null ? '' : String(next))) {
      setEditing(false);
      return;
    }

    // Validate number
    if (type === 'number' && next != null && Number.isNaN(next)) {
      setError('Must be a number.');
      return;
    }

    if (!onSave) {
      // Read-only / propose-only field reached commit somehow — bail.
      setEditing(false);
      return;
    }
    setSaving(true);
    const prevOptimistic = optimistic;
    setOptimistic(next); // optimistic UI

    const { ok, error: errMsg } = await onSave(next);
    setSaving(false);

    if (!ok) {
      setOptimistic(prevOptimistic); // rollback
      setError(errMsg ?? 'Save failed.');
      // keep editing so user can fix or cancel
      return;
    }
    setEditing(false);
    setError(null);
  }

  function scheduleBlurSave() {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    blurTimer.current = window.setTimeout(commit, 400) as unknown as number;
  }

  function cancelBlurSave() {
    if (blurTimer.current) {
      window.clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
      return;
    }
    if (e.key === 'Enter') {
      // Multiline allows newline unless Cmd/Ctrl+Enter
      if (type === 'multiline' && !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      commit();
    }
  }

  const displayValue =
    optimistic == null || optimistic === ''
      ? null
      : sensitive && !reveal
        ? '•'.repeat(Math.min(String(optimistic).length, 8))
        : String(optimistic);

  return (
    <div
      className={full ? 'md:col-span-2' : undefined}
      data-field={fieldKey}
    >
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
        {badge && <FieldBadge text={badge} />}
        {proposalPending && (
          <span className="ml-2 inline-flex items-center rounded bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
            Proposal pending
          </span>
        )}
      </dt>

      {editing && !readOnly ? (
        <dd className="mt-1.5">
          {type === 'multiline' ? (
            <textarea
              autoFocus
              value={draft}
              rows={rows}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={scheduleBlurSave}
              onFocus={cancelBlurSave}
              onKeyDown={handleKey}
              className="w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              placeholder={placeholder}
            />
          ) : type === 'select' && options ? (
            <select
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={scheduleBlurSave}
              onFocus={cancelBlurSave}
              onKeyDown={handleKey}
              className="rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
            >
              <option value="">— select —</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              autoFocus
              type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={scheduleBlurSave}
              onFocus={cancelBlurSave}
              onKeyDown={handleKey}
              className="w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              placeholder={placeholder}
            />
          )}
          {error && (
            <p className="mt-1 text-xs text-st-danger-fg">{error}</p>
          )}
          {saving && (
            <p className="mt-1 text-xs text-tcs-text-muted">Saving…</p>
          )}
        </dd>
      ) : (
        <dd className="mt-1 flex items-center gap-2 text-sm text-tcs-text">
          <span className={sensitive ? 'font-mono' : 'whitespace-pre-wrap break-words'}>
            {displayValue ?? <span className="text-tcs-text-muted italic">{placeholder}</span>}
          </span>
          {sensitive && displayValue != null && (
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="text-xs text-tcs-text-secondary hover:text-tcs-text"
              aria-label={reveal ? `Hide ${label}` : `Reveal ${label}`}
            >
              {reveal ? 'hide' : 'reveal'}
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={startEdit}
              className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded text-tcs-text-secondary hover:bg-tcs-border hover:text-tcs-text"
              aria-label={`Edit ${label}`}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          )}
          {readOnly && onProposeChange && (
            <button
              type="button"
              onClick={onProposeChange}
              className="ml-auto rounded border border-tcs-border bg-tcs-raised px-2 py-0.5 text-xs text-tcs-text hover:bg-tcs-surface"
            >
              Change the rate
            </button>
          )}
          {error && !editing && (
            <p className="mt-1 text-xs text-st-danger-fg">{error}</p>
          )}
        </dd>
      )}
      {hint && <p className="mt-0.5 text-[11px] text-tcs-text-muted">{hint}</p>}
    </div>
  );
}
